import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { TableConnector } from '@loader/shared-types';

import { queryKeys } from '@/lib/queryKeys';
import { throwApiError, useRpc } from '@/lib/rpc';

export type TestFetchResult =
  | { success: true; record_count: number; fields: string[]; preview: Record<string, unknown>[] }
  | { success: false; error: string };

/**
 * Dry-run test of an HTTP fetch config — no resources created.
 * Returns record count, fields, and a 3-row preview on success.
 */
export function useTestHttpFetch() {
  const rpc = useRpc();

  return useMutation({
    mutationFn: async (config: {
      url: string;
      method: 'GET' | 'POST';
      headers?: Record<string, string>;
      body?: string;
      auth_type: 'none' | 'bearer' | 'basic' | 'api_key';
      auth_value?: string;
      auth_header_name?: string;
      response_json_path?: string;
    }) => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.connectors.custom['test-fetch'].$post({
        json: config,
      });
      if (!res.ok) await throwApiError(res, 'Test fetch failed');
      return res.json() as Promise<TestFetchResult>;
    },
  });
}

/**
 * Create a custom connector (HTTP Fetch or SQL Query) + table + first sync.
 */
export function useCreateCustomConnector() {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      type: 'http_fetch' | 'sql' | 'post_api';
      name: string;
      config: Record<string, unknown>;
      schedule?: string;
    }) => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.connectors.custom.create.$post({
        json: data,
      });
      if (!res.ok) await throwApiError(res, 'Failed to create connector');
      return res.json() as Promise<{
        table: { id: string; name: string };
        connector: TableConnector;
      }>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.connectors.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.tables.all });
    },
  });
}

/**
 * Trigger an immediate sync for a custom connector (HTTP Fetch or SQL Query).
 * Provides toast feedback matching the Nango sync trigger UX.
 */
export function useTriggerCustomSync() {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tableId: string) => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.connectors.custom[':tableId']['trigger-sync'].$post({
        param: { tableId },
      });
      if (!res.ok) await throwApiError(res, 'Failed to trigger sync');
      return tableId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.connectors.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.tables.all });
    },
  });
}

/**
 * Update the sync schedule for a custom connector (HTTP Fetch or SQL Query).
 * Pass null to remove the schedule.
 */
export function useUpdateCustomSchedule() {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tableId,
      schedule,
    }: {
      tableId: string;
      schedule: string | null;
    }) => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.connectors.custom[':tableId'].schedule.$patch({
        param: { tableId },
        json: { schedule },
      });
      if (!res.ok) await throwApiError(res, 'Failed to update schedule');
      const json = (await res.json()) as { schedule: string | null };
      return json.schedule;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.connectors.all });
    },
  });
}
